import { forwardRef, useEffect, useId, useRef, useState } from "react";

import { CrossIcon, MinusIcon, PlusIcon } from "@eisox/icons";
import * as RadixSlider from "@radix-ui/react-slider";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import { cn, Tooltip } from "..";
import { parseSliderInput, snapToClosestMark, snapToStep } from "./slider.helper";

const valueBoxClassName =
  "box-border h-6 w-12 rounded-[2px] border p-0 font-medium text-base leading-none text-primary-blue";

interface Mark {
  value: number;
  size?: "small" | "medium" | "large";
  hidden?: boolean;
}

interface SliderProps extends Omit<
  RadixSlider.SliderProps,
  "value" | "defaultValue" | "onValueChange" | "onValueCommit"
> {
  label?: React.ReactNode;
  displayButtons?: boolean;
  displayValue?: boolean;
  undefinedValue?: boolean;
  value?: number;
  defaultValue?: number;
  onValueChange?: (value?: number) => void;
  onValueCommit?: (value?: number) => void;
  valueLabelDisplay?: "on" | "off" | "auto";
  valueLabelFormat?: string | ((value?: number) => React.ReactNode);
  marks?: Mark[];
  classNames?: {
    label?: string;
    root?: string;
    track?: string;
    range?: string;
    thumb?: string;
    tooltip?: string;
    /** The box the value sits in: widen it and pin it to one side to line values up across rows. */
    value?: string;
  };
}

const Slider = forwardRef<HTMLDivElement, SliderProps>(
  (
    {
      label,
      displayButtons = true,
      displayValue = true,
      undefinedValue = false,
      value: valueProp,
      min,
      max,
      step,
      defaultValue,
      onValueChange,
      onValueCommit,
      valueLabelDisplay = "auto",
      valueLabelFormat,
      marks,
      className,
      classNames,
      disabled,
      ...props
    },
    ref,
  ) => {
    const sliderRef = useRef<HTMLSpanElement>(null);
    const valueButtonRef = useRef<HTMLButtonElement>(null);
    const closedFromKeyboardRef = useRef(false);
    const labelId = useId();

    const [value, setValue] = useControllableState<number | undefined>({
      prop: valueProp,
      defaultProp: defaultValue,
      onChange: onValueChange,
    });
    const [isInteracting, setIsInteracting] = useState(false);

    const onPointerDown = () => setIsInteracting(true);
    const onPointerUp = () => setIsInteracting(false);

    const handleClickMinusButton = () =>
      !disabled && setValue(prev => (prev !== undefined ? Math.max(min ?? 0, prev - (step ?? 1)) : undefined));
    const handleClickPlusButton = () =>
      !disabled && setValue(prev => (prev !== undefined ? Math.min(max ?? 100, prev + (step ?? 1)) : undefined));
    const handleDeleteValue = () => setValue(undefined);

    const [draftValue, setDraftValue] = useState<string | null>(null);
    const isEditingValue = draftValue !== null;

    useEffect(() => {
      if (isEditingValue || !closedFromKeyboardRef.current) return;
      closedFromKeyboardRef.current = false;
      valueButtonRef.current?.focus();
    }, [isEditingValue]);

    const handleStartEditingValue = () => setDraftValue(value?.toString() ?? "");
    const handleCancelEditingValue = () => setDraftValue(null);
    const handleCommitDraftValue = () => {
      if (draftValue === null) return;
      setDraftValue(null);
      const parsedValue = parseSliderInput(draftValue);
      if (parsedValue === undefined) return;
      const nextValue =
        marks && marks.length > 0
          ? snapToClosestMark(
              parsedValue,
              marks.map(mark => mark.value),
              min ?? 0,
              max ?? 100,
            )
          : snapToStep(parsedValue, min ?? 0, max ?? 100, step ?? 1);
      setValue(nextValue);
      onValueCommit?.(nextValue);
    };

    const isTooltipOpen = valueLabelDisplay === "on" || (valueLabelDisplay === "auto" && isInteracting);

    const handleChangeValue = (value: number[]) => {
      const currentValue = value[0];
      if (currentValue === undefined) return;
      setValue(
        marks && marks.length > 0
          ? snapToClosestMark(
              currentValue,
              marks.map(mark => mark.value),
              min ?? 0,
              max ?? 100,
            )
          : currentValue,
      );
    };

    function getThumbInBoundsOffset(width: number, left: number, direction: number) {
      const halfWidth = width / 2;
      const halfPercent = 50;
      const offset = linearScale([0, halfPercent], [0, halfWidth]);
      return (halfWidth - offset(left) * direction) * direction;
    }

    function linearScale(input: readonly [number, number], output: readonly [number, number]) {
      return (value: number) => {
        if (input[0] === input[1] || output[0] === output[1]) return output[0];
        const ratio = (output[1] - output[0]) / (input[1] - input[0]);
        return output[0] + ratio * (value - input[0]);
      };
    }

    function convertValueToPercentage(value: number, min: number, max: number) {
      const maxSteps = max - min;
      const percentPerStep = 100 / maxSteps;
      const percentage = percentPerStep * (value - min);
      return percentage;
    }

    return (
      <div className={cn("flex flex-col", className)} ref={ref}>
        {(label ?? displayButtons) && (
          <div className={"mb-1.5 gap-4 flex items-baseline justify-between"}>
            {label && (
              <label id={labelId} className={cn("font-medium text-base text-primary-dark-blue", classNames?.label)}>
                {label}
              </label>
            )}
            <div
              className={`gap-4 font-medium [&>span]:w-7 flex shrink-0 items-center text-base text-primary-blue [&>span]:flex [&>span]:justify-center`}
            >
              {displayButtons && (
                <MinusIcon
                  className={cn(
                    "size-2.5 cursor-pointer stroke-primary-dark-gray",
                    disabled && "cursor-default text-primary-dark-gray",
                  )}
                  onClick={handleClickMinusButton}
                />
              )}
              {displayValue &&
                (isEditingValue ? (
                  <input
                    autoFocus
                    type="text"
                    inputMode="decimal"
                    aria-labelledby={label ? labelId : undefined}
                    className={cn(
                      valueBoxClassName,
                      "border-primary-blue bg-transparent text-center outline-none",
                      classNames?.value,
                    )}
                    value={draftValue}
                    onChange={event => setDraftValue(event.target.value)}
                    onFocus={event => event.target.select()}
                    onBlur={() => {
                      if (!closedFromKeyboardRef.current) handleCommitDraftValue();
                    }}
                    onKeyDown={event => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        closedFromKeyboardRef.current = true;
                        handleCommitDraftValue();
                      }
                      if (event.key === "Escape") {
                        closedFromKeyboardRef.current = true;
                        handleCancelEditingValue();
                      }
                    }}
                  />
                ) : (
                  <button
                    ref={valueButtonRef}
                    type="button"
                    disabled={disabled}
                    className={cn(
                      valueBoxClassName,
                      "flex cursor-text items-center justify-center border-transparent bg-transparent disabled:cursor-default",
                      classNames?.value,
                    )}
                    onClick={handleStartEditingValue}
                  >
                    {typeof value === "number" ? value.toFixed(1) : "--"}
                  </button>
                ))}
              {displayButtons && (
                <PlusIcon
                  className={cn(
                    "size-2.5 cursor-pointer stroke-primary-dark-gray",
                    disabled && "cursor-default text-primary-dark-gray",
                  )}
                  onClick={handleClickPlusButton}
                />
              )}
              {!displayButtons && undefinedValue && (
                <CrossIcon
                  className={cn("size-2.5 cursor-pointer stroke-primary-dark-blue")}
                  onClick={handleDeleteValue}
                />
              )}
            </div>
          </div>
        )}
        <RadixSlider.Root
          {...props}
          ref={sliderRef}
          className={cn("h-5.5 relative flex w-full touch-none items-center select-none", classNames?.root)}
          value={value !== undefined ? [value] : undefined}
          min={min}
          max={max}
          step={step}
          onValueChange={handleChangeValue}
          onValueCommit={newValue => onValueCommit?.(newValue[0])}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          disabled={disabled}
        >
          {marks?.length && (
            <div className={"left-0 absolute flex w-full"}>
              {marks.map((mark, index) => {
                if (mark.hidden) return null;

                const thumbSize = 22;
                const minValue = min ?? 0;
                const maxValue = max ?? 100;
                const markPosition = convertValueToPercentage(mark.value, minValue, maxValue);
                const thumbOffset = getThumbInBoundsOffset(thumbSize, markPosition, 1);

                const sizeClass = mark.size === "small" ? "h-1" : mark.size === "medium" ? "h-2" : "h-3";

                return (
                  <div
                    key={index}
                    className={cn("w-0.25 absolute translate-y-[-50%] bg-primary-blue", sizeClass)}
                    style={{ left: `calc(${markPosition}% + ${thumbOffset}px)` }}
                  />
                );
              })}
            </div>
          )}
          <RadixSlider.Track
            className={cn(
              "h-0.5 relative grow cursor-pointer rounded-full bg-primary-blue/38 data-disabled:bg-primary-dark-gray/38",
              classNames?.track,
            )}
          >
            <RadixSlider.Range
              className={cn(
                "absolute h-full rounded-full bg-primary-blue data-disabled:bg-neutral-800",
                classNames?.range,
              )}
            />
          </RadixSlider.Track>
          <Tooltip.Provider delayDuration={0} disableHoverableContent>
            <Tooltip.Root open={isTooltipOpen}>
              <Tooltip.Trigger asChild>
                <RadixSlider.Thumb
                  className={`relative block h-[22px] w-[22px] cursor-pointer rounded-full border-2 border-primary-blue bg-primary-white not-data-disabled:hover:bg-primary-blue not-data-disabled:hover:after:absolute not-data-disabled:hover:after:top-1/2 not-data-disabled:hover:after:left-1/2 not-data-disabled:hover:after:h-[20px] not-data-disabled:hover:after:w-[20px] not-data-disabled:hover:after:-translate-x-1/2 not-data-disabled:hover:after:-translate-y-1/2 not-data-disabled:hover:after:rounded-full not-data-disabled:hover:after:bg-primary-blue not-data-disabled:hover:after:shadow-[var(--shadow-focus-ring)] not-data-disabled:hover:after:content-[''] not-data-disabled:focus:bg-primary-blue data-disabled:border-neutral-800 data-disabled:before:absolute data-disabled:before:top-1/2 data-disabled:before:left-1/2 data-disabled:before:h-[2px] data-disabled:before:w-[6px] data-disabled:before:-translate-x-1/2 data-disabled:before:-translate-y-1/2 data-disabled:before:bg-neutral-800 data-disabled:before:content-['']`}
                  onMouseEnter={onPointerDown}
                  onMouseLeave={onPointerUp}
                />
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  side="top"
                  className={cn(
                    "px-2 py-1 z-1300 rounded-[2px] bg-primary-dark-blue text-sm text-primary-white",
                    classNames?.tooltip,
                  )}
                >
                  {valueLabelFormat
                    ? typeof valueLabelFormat === "string"
                      ? valueLabelFormat
                      : valueLabelFormat(value)
                    : (value ?? "—")}
                  <Tooltip.Arrow />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          </Tooltip.Provider>
        </RadixSlider.Root>
      </div>
    );
  },
);

export default Slider;
export { Slider };
export type { Mark, SliderProps };
